// File: src/components/forms/CodeField/CodeField.tsx
// Purpose: Form input component

import React, { useState, useRef } from "react";
import styled from "styled-components";
import { RefreshCw, XCircle } from "lucide-react";
import { TextFieldProps } from "../TextField";

/** 🔥 Container to center everything */
const CodeFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/** 🔥 OTP Inputs (Grouped) */
const OTPContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}; 
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxs};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.spacing.xxs}; // ✅ Smaller gaps for mobile
  }
`;

/** 🔥 OTP Input Box */
const OTPInput = styled.input<{ hasError?: boolean }>`
  width: 48px;
  height: 56px;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.bodyLarge.fontSize};
  font-weight: ${({ theme }) => theme.typography.bodyLarge.fontWeight};
  line-height: ${({ theme }) => theme.typography.bodyLarge.lineHeight};
  border: ${({ theme }) => theme.border.sm} solid ${({ theme, hasError }) => (hasError ? theme.colors.error : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surfaceEntry};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.default};

  &:focus {
    border-color: ${({ theme }) => theme.colors.primarySelected};
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 40px;
    height: 50px;
    font-size: ${({ theme }) => theme.typography.body.fontSize};
  }
`;

/** 🔥 Action Container (Regroup buttons & align under last digit) */
const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  max-width: 300px; /* ✅ Matches OTP width */
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

/** 🔄 Buttons */
const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.overline.fontSize};
  font-weight: ${({ theme }) => theme.typography.overline.fontWeight};
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.xxs};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

/** 🔥 Error Message (Fixed height to prevent layout shift) */
const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const ErrorWrapper = styled.div`
  height: 18px;
  width: 100%;
  max-width: 300px;
  display: flex;
  justify-content: center; /* ✅ Center error under OTP */
`;

export interface CodeFieldProps extends Omit<TextFieldProps, "type"> {
  length?: number;
  minLength?: number;
  maxLength?: number;
  validateCode?: (code: string) => boolean | string | null;
  resendCode?: () => void;
  resendText?: string;
  clear?: string;
}

/** 📌 Fully Optimized `CodeField` Component */
export const CodeField: React.FC<CodeFieldProps> = ({
  length = 6,
  minLength = 4,
  maxLength = 6,
  validateCode,
  resendCode = null,
  resendText = "Resend",
  clear = null,
  ...props
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(length).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(new Array(length).fill(null));

  /** 🔄 Handles input change */
  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /** 🔄 Handles backspace/delete navigation */
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /** 📌 Validates OTP on blur */
  const handleBlur = () => {
    setTouched(true);
    const joinedOTP = otp.join("");

    if (joinedOTP.length < minLength) {
      setError(`Code must be at least ${minLength} digits`);
    } else if (joinedOTP.length > maxLength) {
      setError(`Code must be at most ${maxLength} digits`);
    } else if (validateCode) {
      const validationResult = validateCode(joinedOTP);
      setError(typeof validationResult === "string" ? validationResult : null);
    } else {
      setError(null);
    }
  };

  /** 📌 Handles paste event */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pastedText.length > 0) {
      const newOtp = pastedText.split("").slice(0, length);
      setOtp(newOtp.concat(new Array(length - newOtp.length).fill("")));
      inputRefs.current[pastedText.length - 1]?.focus();
    }
    e.preventDefault();
  };

  /** 🔄 Clears OTP */
  const clearOTP = () => {
    setOtp(new Array(length).fill(""));
    setError(null);
    inputRefs.current[0]?.focus();
  };

  return (
    <CodeFieldContainer>
      <OTPContainer>
        {otp.map((digit, index) => (
          <OTPInput
            key={index}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onBlur={handleBlur}
            onPaste={handlePaste}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            hasError={!!error}
          />
        ))}
      </OTPContainer>

      <ErrorWrapper>{error && <ErrorMessage>{error}</ErrorMessage>}</ErrorWrapper>

      {/* 🔄 Resend & Clear Buttons - Centered under the last digit */}
      <ActionContainer>
        {resendCode && (
          <ActionButton onClick={resendCode}>
            <RefreshCw size={16} /> {resendText}
          </ActionButton>
        )}
        {clear && <ActionButton onClick={clearOTP}>
          <XCircle size={16} />
        </ActionButton>}
      </ActionContainer>
    </CodeFieldContainer>
  );
};